import { Loader } from '@terminal/components/loader/Loader';
import { ToastsLayout } from '@terminal/components/toasts-layout/ToastsLayout';
import { appPaths, authPaths } from '@terminal/routes/app-paths';
import { getSession, useAuth } from '@terminal/stores/use-auth/useAuth';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useLivePriceWebSocketStore } from '@terminal/stores/use-live-price-websocket-store/useLivePriceWebSocketStore';
import clsx from 'clsx';
import { FC, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { AppMetadata } from './components';
import { AppHistory } from './components/app-history/AppHistory';
import { Maintenance } from './components/maintenance/Maintenance';
import { MaintenanceBanner } from './components/maintenance-banner/MaintenanceBanner';
import { useTouchDevice } from './hooks/MediaQueryHook';
import { useFdc3 } from './hooks/use-fdc3/useFdc3';
import { useNotificationsListener } from './hooks/use-notifications-listener/useNotificationsListener';
import { useSessionRefresh } from './hooks/use-session-refresh/useSessionRefresh';
import styles from './Root.module.scss';
import { useNavigator } from './stores/use-navigator/useNavigator';
import { useChatNavigation } from './views/alfred/use-chat-navigation/useChatNavigation';
import { MobileNotSupported } from './views/auth/mobile-not-supported/MobileNotSupported';

export const mobileAlfredPaths = [
  authPaths.login,
  appPaths.alfred,
  appPaths.settings.account,
];

export const Root: FC = () => {
  const { authProcessing, initStores, setAuthProcessing } = useAuth();
  const { listenToNavigator } = useNavigator();

  const location = useLocation();
  const isTouchDevice = useTouchDevice();
  const { mode } = useChatNavigation();

  const isIbRoute = location.pathname.includes(appPaths.ib);

  const isLoginPath = location.pathname === authPaths.login;
  const isAlfredPath =
    location.pathname === appPaths.alfred ||
    location.pathname === appPaths.alfredSettings;
  const isAccountSettingsPath = location.pathname === appPaths.settings.account;
  const enableMobileReasoner =
    isLoginPath ||
    isAccountSettingsPath ||
    (isAlfredPath && mode === 'research');

  const isUnsupportedScreenSize =
    isTouchDevice && !isIbRoute && !enableMobileReasoner;
  const { checkIsInMaintenance, getSystemFlags } = useFlags(state => ({
    checkIsInMaintenance: state.checkIsInMaintenance,
    getSystemFlags: state.initialise,
  }));
  useNotificationsListener();
  useSessionRefresh();
  useFdc3();

  const initSession = async () => {
    const session = await getSession();
    await getSystemFlags();

    if (session) {
      await initStores(session);
    }

    setAuthProcessing(false);
  };

  useEffect(() => {
    initSession();

    return () => {
      useLivePriceWebSocketStore.getState().closeConnection();
    };
  }, []);

  useEffect(() => {
    listenToNavigator();
  }, []);

  useEffect(() => {
    const portalEl = document.getElementById('portal');

    if (portalEl) {
      portalEl.style.display = isUnsupportedScreenSize ? 'none' : '';
    }
  }, [isUnsupportedScreenSize]);

  if (checkIsInMaintenance()) {
    return (
      <>
        <Suspense fallback={<Loader />}>
          <Maintenance />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <AppMetadata path={location.pathname} />
      <Suspense fallback={<Loader />}>
        {authProcessing ? (
          <Loader />
        ) : (
          <>
            <MaintenanceBanner />
            <AppHistory>
              <span
                className={clsx(styles.mobileNotSupportedWrapper, {
                  [styles.unsupported]: isUnsupportedScreenSize,
                })}
              >
                <MobileNotSupported />
              </span>
              <span
                className={clsx(styles.outletWrapper, {
                  [styles.unsupported]: isUnsupportedScreenSize,
                })}
              >
                <Outlet />
              </span>
            </AppHistory>
          </>
        )}
      </Suspense>
      <ToastsLayout />
    </>
  );
};
